if (!customElements.get('nn-tablet-preview')) {
  class NNTabletPreview extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.root = this.closest('.nn-product');
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.number = this.root.querySelector('[name="properties[House Number or Name]"]');
      this.street = this.root.querySelector('[name="properties[Street Name]"]');
      this.form = this.root.querySelector('form[action*="/cart/add"]');
      this.finish = this.dataset.finish.toLowerCase();
      this.wall = this.dataset.defaultWall || 'render';
      this.dragonPaths = JSON.parse(this.querySelector('[data-dragon-paths]')?.textContent || '[]').map(d => new Path2D(d));
      this.frontFinish = this.dataset.frontFinish;
      this.optionNames = JSON.parse(this.dataset.optionNames || '[]');
      this.applyFinishes(JSON.parse(this.dataset.variantOptions || '[]'));
      this.font = 'Arial, Helvetica, sans-serif';
      this.weight = 600;
      this.root.dataset.tabletView = 'preview';
      this.root.dataset.previewCase = this.dataset.design === 'welsh' ? 'preserve' : 'uppercase';
      const on = (target, type, fn, capture = false) => target?.addEventListener(type, fn, { signal: this.controller.signal, capture });
      [this.number, this.street].forEach(input => on(input, 'input', () => {
        input.setCustomValidity('');
        this.root.dataset.tabletView = 'preview';
        this.syncViews();
        this.draw();
      }));
      [this.number, this.street].forEach(input => {
        input?.setAttribute('enterkeyhint', 'done');
        if (this.dataset.design === 'welsh') input?.setAttribute('autocapitalize', 'off');
        on(input, 'keydown', event => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          event.stopPropagation();
          input.blur();
        });
      });
      [this.number, this.street].forEach(input => on(input, 'blur', () => { if (this.dataset.design !== 'welsh') input.value = input.value.toUpperCase(); }));
      on(this.form, 'submit', event => {
        if (![this.number, this.street].some(input => input?.value.trim())) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.number.setCustomValidity(this.dataset.required);
          this.number.reportValidity();
          return;
        }
        [this.number, this.street].forEach(input => { if (input) input.value = this.normaliseText(input.value); });
      }, true);
      on(document, 'nn:variant-changed', event => {
        if (event.detail.sectionId !== this.dataset.section) return;
        this.applyFinishes(event.detail.variant.options);
        this.draw();
      });
      this.root.querySelectorAll('[data-view]').forEach(button => on(button, 'click', () => {
        this.root.dataset.tabletView = button.dataset.view;
        this.syncViews();
      }));
      this.photoIndex = Number(this.root.querySelector('.nn-product__dot--active')?.dataset.index || 0);
      on(document, 'nn:gallery-changed', event => {
        if (event.detail.sectionId !== this.dataset.section) return;
        this.photoIndex = event.detail.index;
        this.syncViews();
      });
      this.root.querySelectorAll('[data-photo-index]').forEach(button => on(button, 'click', () => {
        this.root.dataset.tabletView = 'photos';
        this.photoIndex = Number(button.dataset.photoIndex);
        this.root.querySelector(`.nn-product__dot[data-index="${this.photoIndex}"]`)?.click();
        this.syncViews();
      }));
      this.syncViews();
      if (this.dataset.design === 'welsh') {
        this.surfacePhoto = new Image();
        this.surfacePhoto.crossOrigin = 'anonymous';
        on(this.surfacePhoto, 'load', () => { this.dataset.surface = 'photographic'; this.draw(); });
        this.surfacePhoto.src = 'https://cdn.shopify.com/s/files/1/0929/0350/6255/files/nn-tablet-render-preview-base.png?v=1791019351';
      }
      this.querySelectorAll('[data-wall]').forEach(button => on(button, 'click', () => {
        this.wall = button.dataset.wall;
        this.querySelectorAll('[data-wall]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
        this.querySelectorAll('[data-wall-image]').forEach(img => { img.hidden = img.dataset.wallImage !== this.wall; });
        this.draw();
      }));
      this.querySelectorAll('[data-wall-image]').forEach(img => on(img, 'error', () => {
        this.querySelector('[data-caption]').textContent = this.dataset.imageError;
      }));
      this.draw();
      const loadAdobe = () => Promise.all([document.fonts.load('400 88px paralucent'), document.fonts.load('500 88px paralucent-stencil')]).then(([faces, stencilFaces]) => {
        if (!faces.length || !this.isConnected) return;
        this.font = 'paralucent, Arial, sans-serif';
        this.weight = 400;
        this.dataset.loadedFont = 'paralucent-400';
        this.stencilReady = stencilFaces.length > 0;
        this.dataset.loadedStencil = String(this.stencilReady);
        this.draw();
      }).catch(() => {});
      const adobeLink = document.querySelector('[data-tablet-adobe-font]');
      on(adobeLink, 'load', loadAdobe);
      loadAdobe();
      if (this.dataset.fontUrl) {
        const face = new FontFace('NNTabletLettering', `url(${JSON.stringify(this.dataset.fontUrl)})`, { weight: '500' });
        face.load().then(loaded => {
          document.fonts.add(loaded);
          this.font = 'NNTabletLettering, Arial, sans-serif';
          this.weight = 500;
          if (this.isConnected) this.draw();
        }).catch(() => {});
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    syncViews() {
      this.root.querySelectorAll('[data-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.view === this.root.dataset.tabletView)));
      this.root.querySelectorAll('[data-photo-index]').forEach(button => button.setAttribute('aria-pressed', String(this.root.dataset.tabletView === 'photos' && Number(button.dataset.photoIndex) === this.photoIndex)));
    }
    layer() {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 1254;
      return canvas;
    }
    applyFinishes(options) {
      const frontIndex = this.optionNames.findIndex(name => /front/i.test(name));
      const backIndex = this.optionNames.findIndex(name => /back/i.test(name));
      const front = frontIndex >= 0 ? options[frontIndex] : this.dataset.frontFinish;
      this.frontFinish = /gr[ae]y/i.test(front) ? 'grey' : 'black';
      this.dataset.front = this.frontFinish;
      const sizeIndex = this.optionNames.findIndex(name => /size/i.test(name));
      this.largeSize = sizeIndex >= 0 && /35/.test(options[sizeIndex]);
      if (this.dataset.design === 'welsh') this.querySelector('.nn-tablet-size').textContent = this.largeSize ? '35 × 18 cm' : '25 × 15 cm';
      if (backIndex >= 0 && options[backIndex]) this.finish = options[backIndex].toLowerCase();
    }
    textRuns(ctx, text, size) {
      let advance = 0, left = Infinity, right = -Infinity, ascent = 0, descent = 0;
      const runs = (text.match(/[04689]+|[^04689]+/g) || []).map(value => {
        const stencil = this.stencilReady && /^[04689]+$/.test(value);
        const font = stencil ? `500 ${size}px paralucent-stencil` : `${this.weight} ${size}px ${this.font}`;
        ctx.font = font;
        const metrics = ctx.measureText(value), x = advance;
        left = Math.min(left, x - metrics.actualBoundingBoxLeft);
        right = Math.max(right, x + metrics.actualBoundingBoxRight);
        ascent = Math.max(ascent, metrics.actualBoundingBoxAscent);
        descent = Math.max(descent, metrics.actualBoundingBoxDescent);
        advance += metrics.width;
        return { value, font, x };
      });
      return { runs, width: advance, actualBoundingBoxLeft: -left, actualBoundingBoxRight: right, actualBoundingBoxAscent: ascent, actualBoundingBoxDescent: descent };
    }
    fitText(ctx, text, box, maxSize, align = 'center', measureOnly = false) {
      let size = maxSize;
      let m = this.textRuns(ctx, text, size);
      const width = Math.max(m.width, m.actualBoundingBoxLeft + m.actualBoundingBoxRight);
      size *= Math.min(1, box.w / width, box.h / (m.actualBoundingBoxAscent + m.actualBoundingBoxDescent));
      m = this.textRuns(ctx, text, size);
      const squeeze = 1;
      const x = box.x + (align === 'left' ? 0 : (box.w - (m.actualBoundingBoxLeft + m.actualBoundingBoxRight) * squeeze) / 2) + m.actualBoundingBoxLeft * squeeze;
      const y = box.y + (box.h - m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2 + m.actualBoundingBoxAscent;
      if (measureOnly) return { left: x - m.actualBoundingBoxLeft, right: x + m.actualBoundingBoxRight };
      ctx.save();ctx.translate(x, y);
      m.runs.forEach(run => { ctx.font = run.font; ctx.fillText(run.value, run.x, 0); });
      ctx.restore();
    }
    splitStreet(ctx, text, width) {
      ctx.font = `${this.weight} 88px ${this.font}`;
      const words = text.split(/\s+/);
      if (words.length < 2 || this.textRuns(ctx, text, 88).width <= width) return [text];
      let best = [text], score = Infinity;
      for (let i = 1; i < words.length; i++) {
        const lines = [words.slice(0, i).join(' '), words.slice(i).join(' ')];
        const widths = lines.map(line => this.textRuns(ctx, line, 88).width);
        const cost = Math.max(...widths) + Math.abs(widths[0] - widths[1]) * .18;
        if (cost < score) { score = cost; best = lines; }
      }
      return best;
    }
    welshGeometry() {
      const w = 900, h = this.largeSize ? w * 18 / 35 : w * 15 / 25;
      return { x: (1254 - w) / 2, y: 620 - h / 2, w, h };
    }
    welshMask(m, first, second) {
      const fields = [first, second].filter(Boolean);
      let number = '', names = [];
      fields.forEach(value => {
        const match = value.match(/^(\d+[A-Z]?(?:[-/]\d+[A-Z]?)?)(?:\s+(.+))?$/i);
        if (!number && match) { number = match[1]; if (match[2]) names.push(match[2]); }
        else names.push(value);
      });
      const name = names.join(' '), p = this.welshGeometry(), pad = 48, inner = p.w - pad * 2;
      const divider = p.y + p.h * (number ? .665 : .715);
      const dragonBox = number ? { x: p.x + p.w * .59, y: p.y + 48, w: p.w * .355, h: divider - p.y - 78 } : { x: p.x + pad, y: p.y + 42, w: inner, h: divider - p.y - 68 };
      const scale = Math.min(dragonBox.w / 269.17, dragonBox.h / 185.985);
      m.save();
      m.translate(dragonBox.x + (dragonBox.w - 269.17 * scale) / 2, dragonBox.y + (dragonBox.h - 185.985 * scale) / 2);
      m.scale(scale, scale); m.translate(-382.54, -13.06);
      this.dragonPaths.forEach(path => m.fill(path)); m.restore();
      if (number) this.fitText(m, number, { x: p.x + pad, y: p.y + 48, w: p.w * .45, h: divider - p.y - 79 }, 380, 'left');
      m.beginPath();m.roundRect(p.x + pad, divider, inner, 9, 4.5);m.fill();
      if (name) {
        this.fitText(m, name, { x: p.x + pad, y: divider + 30, w: inner, h: p.y + p.h - 42 - divider - 30 }, 108);
      }
      this.dataset.layout = number ? 'number' : 'name';
      this.dataset.size = this.largeSize ? '35x18' : '25x15';
    }
    welshPanel(ctx) {
      const p = this.welshGeometry();
      ctx.save();
      ctx.fillStyle = '#20282b';
      ctx.shadowColor = '#15120d66';ctx.shadowBlur = 13;ctx.shadowOffsetX = 40;ctx.shadowOffsetY = 57;
      ctx.beginPath();ctx.roundRect(p.x + 1, p.y + 7, p.w, p.h, 18);ctx.fill();
      ctx.shadowColor = '#11130f77';ctx.shadowBlur = 26;ctx.shadowOffsetX = 9;ctx.shadowOffsetY = 17;ctx.fill();
      ctx.shadowColor = 'transparent';
      const edge = ctx.createLinearGradient(p.x,p.y,p.x,p.y+p.h+8);
      edge.addColorStop(0,'#697176');edge.addColorStop(.75,'#343d42');edge.addColorStop(1,'#242a2c');
      ctx.fillStyle = edge;ctx.fill();
      ctx.beginPath();ctx.roundRect(p.x,p.y,p.w,p.h,18);
      ctx.save();ctx.clip();
      ctx.fillStyle = '#505c65';ctx.fillRect(p.x,p.y,p.w,p.h);
      if (this.surfacePhoto?.complete && this.surfacePhoto.naturalWidth) {
        ctx.drawImage(this.surfacePhoto,200,396,858,430,p.x,p.y,p.w,p.h);
        ctx.globalCompositeOperation = 'screen';ctx.fillStyle = '#394652';ctx.fillRect(p.x,p.y,p.w,p.h);
        ctx.globalCompositeOperation = 'source-over';
      }
      const sheen = ctx.createLinearGradient(p.x,p.y,p.x+p.w*.8,p.y+p.h);
      sheen.addColorStop(0,'#ffffff0d');sheen.addColorStop(.48,'#ffffff00');sheen.addColorStop(1,'#080b1012');
      ctx.fillStyle = sheen;ctx.fillRect(p.x,p.y,p.w,p.h);ctx.restore();
      const rim = ctx.createLinearGradient(p.x,p.y,p.x+p.w,p.y+p.h);
      rim.addColorStop(0,'#edf2f34d');rim.addColorStop(.4,'#ffffff0a');rim.addColorStop(1,'#00000040');
      ctx.lineWidth = 1.2;ctx.strokeStyle = rim;ctx.stroke();ctx.restore();
    }
    normaliseText(value = '') {
      const text = value.trim();
      return this.dataset.design === 'welsh' ? text : text.toUpperCase();
    }
    draw() {
      let number = this.normaliseText(this.number?.value);
      let street = this.normaliseText(this.street?.value).replace(/\s+/g, ' ');
      const example = !number && !street;
      if (example) { number = '15'; street = this.dataset.exampleStreet || 'VICTORIA AVENUE'; }
      const mask = this.layer(), m = mask.getContext('2d');
      m.fillStyle = '#fff';
      if (this.dataset.design === 'welsh') {
        this.welshMask(m, number, street);
      } else if (number && street) {
        const lines = this.splitStreet(m, street, 393);
        const widest = Math.max(...lines.map(line => this.textRuns(m, line, 88).width));
        const baseSize = Math.min(88, 88 * 393 / widest);
        const growth = Math.max(1, Math.min(1.35, 297 / widest));
        const size = baseSize * growth;
        const numberWidth = 323 * 1.05;
        const numberHeight = 290 * 1.05;
        const numberBox = { x: 0, y: 604 - numberHeight / 2, w: numberWidth, h: numberHeight };
        const lineHeight = 82 * growth;
        const lineStep = size * 1.3;
        const blockHeight = lineHeight + (lines.length - 1) * lineStep;
        const centreY = lines.length === 1 ? 589 : 609.5;
        const streetBoxes = lines.map((line, index) => ({ x: 0, y: centreY - blockHeight / 2 + index * lineStep, w: 393, h: lineHeight }));
        const numberBounds = this.fitText(m, number, numberBox, 410 * 1.05, 'center', true);
        const streetBounds = lines.map((line, index) => this.fitText(m, line, streetBoxes[index], size, 'left', true));
        const numberInkWidth = numberBounds.right - numberBounds.left;
        const streetInkWidth = Math.max(...streetBounds.map(bounds => bounds.right - bounds.left));
        const gap = (858 - numberInkWidth - streetInkWidth - 12) / 4;
        numberBox.x = 200 + gap - numberBounds.left;
        const dividerX = 200 + gap * 2 + numberInkWidth;
        streetBoxes.forEach(box => { box.x = dividerX + 12 + gap; });
        this.fitText(m, number, numberBox, 410 * 1.05);
        m.fillRect(dividerX, 455, 12, 304);
        lines.forEach((line, index) => this.fitText(m, line, streetBoxes[index], size, 'left'));
        this.dataset.streetScale = growth.toFixed(3);
        this.dataset.numberAlignment = 'panel-centre';
        this.dataset.streetLineHeight = '130%';
        this.dataset.dividerX = dividerX.toFixed(1);
        this.dataset.layoutGap = gap.toFixed(2);
      } else if (number) {
        this.fitText(m, number, { x: 248, y: 459, w: 757, h: 290 }, 410);
      } else {
        const lines = this.splitStreet(m, street, 757);
        m.font = `${this.weight} 110px ${this.font}`;
        const size = Math.min(110, 110 * 757 / Math.max(...lines.map(line => this.textRuns(m, line, 110).width)));
        lines.forEach((line, index) => this.fitText(m, line, { x: 248, y: lines.length === 1 ? 542 : 491 + index * 118, w: 757, h: 100 }, size, 'left'));
      }
      const white = this.finish.includes('white'), gold = this.finish.includes('gold'), red = this.finish.includes('red');
      const material = this.layer(), paint = material.getContext('2d');
      const gradient = paint.createLinearGradient(250, 440, 920, 800);
      const stops = red ? [[0, '#e55256'], [.21, '#ff7373'], [.40, '#b80c26'], [.46, '#590319'], [.49, '#d3203b'], [.66, '#ff6065'], [1, '#94091f']] : white ? [[0, '#e6e5df'], [.45, '#f4f3ef'], [1, '#e5e5df']] : gold ? [[0, '#ede0ad'], [.21, '#faf0c8'], [.40, '#d1b367'], [.46, '#8a743d'], [.49, '#e2c77e'], [.66, '#fff0ba'], [1, '#bda05c']] : [[0, '#eef1f1'], [.21, '#f9fbfc'], [.40, '#cbd2d5'], [.46, '#7f898b'], [.49, '#d3dcde'], [.66, '#f7fafb'], [1, '#b6c0c3']];
      stops.forEach(([offset, colour]) => gradient.addColorStop(offset, colour));
      paint.fillStyle = gradient;
      paint.fillRect(0, 0, 1254, 1254);
      if (!white) {
        paint.save();
        paint.filter = 'blur(14px)';
        paint.fillStyle = red ? '#42091d66' : gold ? '#39412b55' : '#34433155';
        const trees = [[315,740,30,54],[685,674,22,54],[780,561,22,38],[972,661,39,73],[1021,593,13,70]];
        trees.forEach(([x,y,rx,ry]) => { paint.beginPath(); paint.ellipse(x,y,rx,ry,-.18,0,Math.PI*2);paint.fill(); });
        paint.restore();
        paint.save();paint.globalAlpha = .23;paint.fillStyle = '#fff';paint.transform(1, 0, -.2, 1, 0, 0);paint.fillRect(605, 400, 25, 400);paint.fillRect(1030, 400, 11, 400);paint.restore();
      }
      paint.globalCompositeOperation = 'destination-in';paint.drawImage(mask, 0, 0);
      const ctx = this.ctx;
      ctx.clearRect(0, 0, 1254, 1254);
      if (this.dataset.design === 'welsh') this.welshPanel(ctx);
      ctx.save();ctx.shadowColor = '#000';ctx.shadowBlur = 2.4;ctx.shadowOffsetX = -.6;ctx.shadowOffsetY = -1.3;ctx.drawImage(material, 0, 0);ctx.restore();
      const edge = this.layer(), e = edge.getContext('2d');
      e.drawImage(mask, 0, 0);e.globalCompositeOperation = 'destination-out';e.drawImage(mask, this.dataset.design === 'welsh' ? 1.2 : 2, this.dataset.design === 'welsh' ? 4 : 6);
      e.globalCompositeOperation = 'source-in';e.fillStyle = this.dataset.design === 'welsh' ? '#111416e6' : '#080a0af0';e.fillRect(0, 0, 1254, 1254);
      ctx.drawImage(edge, 0, 0);
      e.clearRect(0, 0, 1254, 1254);e.globalCompositeOperation = 'source-over';e.drawImage(mask,0,0);e.globalCompositeOperation = 'destination-out';e.drawImage(mask,-1.2,-1.7);e.globalCompositeOperation='source-in';e.fillStyle = this.dataset.design === 'welsh' ? '#ffffff38' : '#ffffff70';e.fillRect(0,0,1254,1254);ctx.drawImage(edge,0,0);
      const finishLabel = this.root.querySelector('.nn-product__panel-card--selected .nn-product__panel-name')?.textContent || this.finish;
      this.canvas.setAttribute('aria-label', `${number} ${street}, ${this.frontFinish} front, ${finishLabel}, ${this.wall}`);
      this.querySelector('[data-caption]').textContent = example ? this.dataset.example : this.dataset.personalised;
    }
  }
  customElements.define('nn-tablet-preview', NNTabletPreview);
}
